/**
 * Which questionnaire block an answer belongs to, and in which order the
 * blocks and their questions are read - for the staff side.
 *
 * F138b: the partner's answers pane used to hold THREE hand-typed copies of
 * the catalog order (`Q1_ANSWER_ORDER` and friends in ConsolePanels.tsx) and
 * all three had drifted from the questionnaire itself: `q1_17_lexicon` and the
 * six questions fix-q2-details added (q2_53..q2_58) were in no list, so they
 * sorted to the bottom in a lump, and two questions F88 retired were still
 * listed. A partner reading 63 cards was reading them partly out of order.
 *
 * The catalog itself lives in `worker/src/questionnaire.ts`, which imports
 * Hono and therefore cannot enter the browser bundle. So this file mirrors it
 * and `tests/vystupy-f138.test.ts` asserts the mirror EXACTLY - same keys,
 * same order, same chapter, in both q2 contract versions. A question added to
 * the questionnaire and forgotten here fails the gate.
 */
export type AnswerChapter =
  | "business"
  | "persona"
  | "setup"
  | "miniprofile"
  | "about"
  | "voice"
  | "customer"
  | "story_v2"
  | "beliefs"
  | "value"
  | "proof"
  | "pricing"
  | "launch_v2"
  | "brand"
  | "story"
  | "clients"
  | "offer"
  | "launch";

export interface AnswerCatalogEntry {
  key: string;
  chapter: AnswerChapter;
}

export const Q1_ANSWER_CATALOG: readonly AnswerCatalogEntry[] = [
  { key: "q1_01_business_essence_and_value", chapter: "business" },
  { key: "q1_03_target_client", chapter: "business" },
  { key: "q1_05_method_or_framework", chapter: "business" },
  { key: "q1_17_lexicon", chapter: "business" },
  { key: "q1_06_boundaries", chapter: "business" },
  { key: "q1_12_never_advise", chapter: "business" },
  { key: "q1_02_twin_mode", chapter: "persona" },
  { key: "q1_04_voice_and_impression", chapter: "persona" },
  { key: "q1_11_addressing_and_lexicon", chapter: "persona" },
  { key: "q1_16_expression_style", chapter: "persona" },
  { key: "q1_09_first_message", chapter: "persona" },
  { key: "q1_15_situational_replies", chapter: "persona" },
  { key: "q1_14_initiated_messages", chapter: "persona" },
  { key: "q1_07_buddy_name", chapter: "setup" },
  { key: "q1_08_voice_and_meditation", chapter: "setup" },
  { key: "q1_13_profile_photo", chapter: "setup" },
  { key: "q1_10_google_account", chapter: "setup" }
];

export const Q2_V1_ANSWER_CATALOG: readonly AnswerCatalogEntry[] = [
  { key: "q2_01_origin_story", chapter: "story" },
  { key: "q2_02_intent_and_success", chapter: "story" },
  { key: "q2_03_enemy_and_beliefs", chapter: "story" },
  { key: "q2_17_big_idea", chapter: "story" },
  { key: "q2_04_client_stories", chapter: "clients" },
  { key: "q2_05_objections", chapter: "clients" },
  { key: "q2_20_moment_of_need", chapter: "clients" },
  { key: "q2_06_proof", chapter: "proof" },
  { key: "q2_07_authority_numbers", chapter: "proof" },
  { key: "q2_19_sales_photos", chapter: "proof" },
  { key: "q2_18_ai_value", chapter: "offer" },
  { key: "q2_21_owner_usage", chapter: "offer" },
  { key: "q2_08_products_prices", chapter: "offer" },
  { key: "q2_09_one_on_one_value", chapter: "offer" },
  { key: "q2_10_price", chapter: "offer" },
  { key: "q2_11_first_audience", chapter: "launch" },
  { key: "q2_12_audience_size", chapter: "launch" },
  { key: "q2_13_launch_experience", chapter: "launch" },
  { key: "q2_14_involvement", chapter: "launch" },
  { key: "q2_22_beta_group", chapter: "launch" },
  { key: "q2_15_sales_model", chapter: "launch" },
  { key: "q2_16_stripe", chapter: "launch" }
];

export const Q2_V2_ANSWER_CATALOG: readonly AnswerCatalogEntry[] = [
  { key: "q2_m01_database", chapter: "miniprofile" },
  { key: "q2_m02_socials", chapter: "miniprofile" },
  { key: "q2_m03_allies", chapter: "miniprofile" },
  { key: "q2_m04_practice", chapter: "miniprofile" },
  { key: "q2_m05_infoproducts", chapter: "miniprofile" },
  { key: "q2_m06_campaign", chapter: "miniprofile" },
  { key: "q2_m07_payments", chapter: "miniprofile" },
  { key: "q2_m08_promo", chapter: "miniprofile" },
  { key: "q2_07_authority_numbers", chapter: "about" },
  { key: "q2_02_intent_and_success", chapter: "about" },
  // F152-W3: the voice block. Only a sales_only client is ASKED these (the
  // `journeys` axis of the catalog), but the chapter is listed unconditionally
  // here on purpose: this file decides where a STORED answer is read, and an
  // answer that exists must have a home whatever the client's journey says
  // today. A full client simply has none of these rows.
  { key: "q2_59_buddy_name", chapter: "voice" },
  { key: "q2_60_addressing_and_lexicon", chapter: "voice" },
  { key: "q2_61_voice_and_impression", chapter: "voice" },
  { key: "q2_62_expression_style", chapter: "voice" },
  { key: "q2_63_situational_replies", chapter: "voice" },
  { key: "q2_64_method_or_framework", chapter: "voice" },
  { key: "q2_65_lexicon", chapter: "voice" },
  { key: "q2_66_initiated_messages", chapter: "voice" },
  { key: "q2_31_typical_customer", chapter: "customer" },
  { key: "q2_32_customer_words", chapter: "customer" },
  { key: "q2_33_customer_goals", chapter: "customer" },
  { key: "q2_34_customer_obstacles", chapter: "customer" },
  { key: "q2_53_failed_solutions", chapter: "customer" },
  { key: "q2_35_not_for", chapter: "customer" },
  { key: "q2_36_backstory", chapter: "story_v2" },
  { key: "q2_37_breakthrough", chapter: "story_v2" },
  { key: "q2_38_old_ways", chapter: "story_v2" },
  { key: "q2_39_belief_vehicle", chapter: "beliefs" },
  { key: "q2_40_belief_internal", chapter: "beliefs" },
  { key: "q2_41_belief_external", chapter: "beliefs" },
  { key: "q2_42_big_idea", chapter: "beliefs" },
  { key: "q2_43_use_cases", chapter: "value" },
  { key: "q2_44_value_stack", chapter: "value" },
  { key: "q2_04_client_stories", chapter: "proof" },
  { key: "q2_06_proof", chapter: "proof" },
  { key: "q2_19_sales_photos", chapter: "proof" },
  { key: "q2_54_writing_style_samples", chapter: "proof" },
  { key: "q2_55_big_promise", chapter: "pricing" },
  { key: "q2_46_sales_intent", chapter: "pricing" },
  { key: "q2_47_value_economics", chapter: "pricing" },
  { key: "q2_56_price_history", chapter: "pricing" },
  { key: "q2_48_price_thinking", chapter: "pricing" },
  { key: "q2_50_deadline_reason", chapter: "pricing" },
  { key: "q2_11_first_audience", chapter: "launch_v2" },
  { key: "q2_13_launch_experience", chapter: "launch_v2" },
  { key: "q2_23_red_lines", chapter: "launch_v2" },
  { key: "q2_14_involvement", chapter: "launch_v2" },
  { key: "q2_28_acquisition", chapter: "launch_v2" },
  { key: "q2_57_expected_first_sales", chapter: "launch_v2" },
  { key: "q2_58_longterm_promo", chapter: "launch_v2" },
  { key: "q2_24_brand_face", chapter: "brand" },
  { key: "q2_52_design_inputs", chapter: "brand" },
  { key: "q2_26_constraints", chapter: "brand" }
];

/** The catalog a stored answer row belongs to. q2 has two contract versions. */
export function answerCatalogFor(questionnaire: string, questionVersion?: number): readonly AnswerCatalogEntry[] {
  if (questionnaire === "q1") return Q1_ANSWER_CATALOG;
  return Number(questionVersion) === 2 ? Q2_V2_ANSWER_CATALOG : Q2_V1_ANSWER_CATALOG;
}

/**
 * Sort rank of one stored answer: q1 before q2, then the question's own place
 * in its catalog. A key the catalog does not know (an answer written under an
 * older questionnaire) keeps its honest place at the END of its questionnaire
 * rather than being dropped - the pane still shows it, named as legacy.
 */
export function answerSortRank(row: { questionnaire?: unknown; itemKey?: unknown; questionVersion?: unknown }): number {
  const questionnaire = String(row.questionnaire);
  const catalog = answerCatalogFor(questionnaire, Number(row.questionVersion));
  const index = catalog.findIndex((entry) => entry.key === String(row.itemKey));
  return (questionnaire === "q1" ? 0 : 10_000) + (index >= 0 ? index : catalog.length);
}

/** The chapter a stored answer belongs to, or null for a legacy key. */
export function answerChapterOf(row: { questionnaire?: unknown; itemKey?: unknown; questionVersion?: unknown }): AnswerChapter | null {
  const catalog = answerCatalogFor(String(row.questionnaire), Number(row.questionVersion));
  return catalog.find((entry) => entry.key === String(row.itemKey))?.chapter ?? null;
}
